import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  OnDestroy,
  ViewChild,
  computed,
  input,
  model,
  output,
} from '@angular/core';

export type RangeTrackVariant = 'mint' | 'warmth' | 'neutral' | 'brightness';

/**
 * RangeSliderComponent — accessible slider primitive used by Screen Fill
 * Light (brightness + warmth) and as a fallback elsewhere.
 *
 * Pointer + keyboard support. The visual track + fill + thumb is rendered
 * by us so it shares recorder design tokens. The `warmth` variant renders
 * a cool→warm gradient under the thumb.
 */
@Component({
  selector: 'rec-range-slider',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './range-slider.component.html',
  styleUrls: ['./range-slider.component.scss'],
})
export class RangeSliderComponent implements OnDestroy {
  readonly min = input<number>(0);
  readonly max = input<number>(100);
  readonly step = input<number>(1);
  readonly value = model.required<number>();
  readonly label = input<string>('');
  readonly disabled = input<boolean>(false);
  readonly trackVariant = input<RangeTrackVariant>('mint');
  /** Optional formatter used for aria-valuetext. */
  readonly format = input<((v: number) => string) | null>(null);

  readonly change = output<number>();
  readonly commit = output<number>();

  @ViewChild('track', { static: true })
  private trackEl!: ElementRef<HTMLDivElement>;

  @HostBinding('attr.data-disabled')
  get disabledAttr() {
    return this.disabled() ? 'true' : null;
  }

  @HostBinding('attr.data-variant')
  get variantAttr() {
    return this.trackVariant();
  }

  readonly fillPct = computed(() => {
    const range = this.max() - this.min();
    if (range <= 0) return 0;
    return ((this.value() - this.min()) / range) * 100;
  });

  readonly valueText = computed(() => {
    const f = this.format();
    return f ? f(this.value()) : String(this.value());
  });

  private _moveListener: ((e: PointerEvent) => void) | null = null;
  private _upListener: ((e: PointerEvent) => void) | null = null;

  @HostListener('pointerdown', ['$event'])
  onPointerDown(ev: PointerEvent): void {
    if (this.disabled()) return;
    // If a stale drag is somehow still attached, clear it first.
    this._detachDocListeners();
    ev.preventDefault();
    this.updateFromPointer(ev);

    // Listen on the document so pointermove/up fire reliably even when the
    // pointer leaves the slider — and so a missed pointerup can never leave
    // the slider stuck in "follow my cursor" mode.
    this._moveListener = (e: PointerEvent) => {
      if (this.disabled()) return;
      this.updateFromPointer(e);
    };
    this._upListener = () => {
      this._detachDocListeners();
      this.commit.emit(this.value());
    };
    document.addEventListener('pointermove', this._moveListener);
    document.addEventListener('pointerup', this._upListener);
    document.addEventListener('pointercancel', this._upListener);
  }

  private _detachDocListeners(): void {
    if (this._moveListener) {
      document.removeEventListener('pointermove', this._moveListener);
      this._moveListener = null;
    }
    if (this._upListener) {
      document.removeEventListener('pointerup', this._upListener);
      document.removeEventListener('pointercancel', this._upListener);
      this._upListener = null;
    }
  }

  ngOnDestroy(): void {
    this._detachDocListeners();
  }

  @HostListener('keydown', ['$event'])
  onKey(ev: KeyboardEvent): void {
    if (this.disabled()) return;
    const s = this.step();
    const big = Math.max(s * 10, (this.max() - this.min()) / 10);
    let next: number | null = null;
    switch (ev.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        next = this.value() + s;
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        next = this.value() - s;
        break;
      case 'PageUp':
        next = this.value() + big;
        break;
      case 'PageDown':
        next = this.value() - big;
        break;
      case 'Home':
        next = this.min();
        break;
      case 'End':
        next = this.max();
        break;
    }
    if (next !== null) {
      ev.preventDefault();
      this.setValue(next);
      this.commit.emit(this.value());
    }
  }

  private updateFromPointer(ev: PointerEvent): void {
    const rect = this.trackEl.nativeElement.getBoundingClientRect();
    const pct = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width));
    const raw = this.min() + pct * (this.max() - this.min());
    this.setValue(raw);
  }

  private setValue(raw: number): void {
    const s = this.step();
    const snapped = Math.round((raw - this.min()) / s) * s + this.min();
    const clamped = Math.max(this.min(), Math.min(this.max(), snapped));
    if (clamped !== this.value()) {
      this.value.set(clamped);
      this.change.emit(clamped);
    }
  }
}
